import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

/**
 *  ADMIN → KRÉTA → KRÉTA ÖNTESZT (backend: kreta-atvitel.js + naplo-kreta-naploz.js).
 *
 *  2026-11-02, a user: „a kréta önteszt menüt az adminban úgy hozzuk létre, hogy az összes
 *  funkciót tudjam tesztelni, hogy működik-e”. Az Európa 2000-nél 10-21 óta egyetlen KRÉTA-
 *  feladat sem futott le, és ez csak a futásnaplókból derült ki. Ez a lap ezért két részből áll:
 *
 *    1. az ütemezett feladatok utolsó futásai – megnyitáskor, KRÉTA-belépés nélkül;
 *    2. az önteszt funkciónként: a belépőlap, egy tanári és egy adminisztrációs fiók belépése,
 *       majd csak olvasó KRÉTA-hívások, és az adatbázisból a helyettesítések és az új tanulók
 *       sorsa. A KRÉTÁ-BA SEMMIT NEM ÍR.
 */
@Component({
  selector: 'app-admin-kreta-onteszt',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, RouterModule],
  templateUrl: './kreta-onteszt.component.html',
  styleUrls: ['./kreta-onteszt.component.scss'],
})
export class AdminKretaOntesztComponent implements OnInit, OnDestroy {
  intezmeny = '';
  feladatok: any[] = [];
  feladatHiba = '';
  feladatToltodik = false;

  fiokok: { allomany: string; tanar_nev: string | null; kreta_user: string; aktiv: boolean }[] = [];
  tanarFiok = '';
  adminFiok = '';
  onteszt: any = null;
  inditas = false;
  private idozito: any = null;

  /** A funkciók sorrendje a felületen – az ellenőrzések `adatkor` mezője szerint. */
  readonly FUNKCIOK: { kulcs: string; cim: string }[] = [
    { kulcs: 'kapcsolat', cim: 'Belépés' },
    { kulcs: 'ora', cim: 'Óra + téma (naplózás)' },
    { kulcs: 'helyettesites', cim: 'Helyettesítés' },
    { kulcs: 'jegy', cim: 'Évközi és negyedéves jegy' },
    { kulcs: 'mulasztas', cim: 'Mulasztás' },
    { kulcs: 'igazolas', cim: 'Igazolás' },
    { kulcs: 'feljegyzes', cim: 'Feljegyzés' },
    { kulcs: 'orarend', cim: 'Órarend-átvétel' },
    { kulcs: 'tagsag', cim: 'Csoporttagság' },
    { kulcs: 'tanuloadat', cim: 'Tanulói adatok' },
    { kulcs: 'ujtanulo', cim: 'Új tanuló' },
  ];
  readonly allapotNev: Record<string, string> = {
    rendben: 'Rendben', figyelmeztetes: 'Figyelmeztetés', hiba: 'Hiba', nincs_adat: 'Nem ellenőrizhető',
  };

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const si: any = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(si?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    await Promise.all([this.feladatBetolt(), this.fiokBetolt(), this.allapotBetolt()]);
  }

  ngOnDestroy() { this.leallit(); }

  async feladatBetolt() {
    if (!this.intezmeny) return;
    this.feladatToltodik = true;
    this.feladatHiba = '';
    try {
      const r: any = await firstValueFrom(this.http.get(`/api/kreta-atvitel/utemezett/${this.intezmeny}`));
      this.feladatok = Array.isArray(r) ? r : [];
    } catch (e: any) {
      this.feladatHiba = e?.error?.error || 'Az ütemezett feladatok állapota nem tölthető be.';
    } finally {
      this.feladatToltodik = false;
    }
  }

  async fiokBetolt() {
    if (!this.intezmeny) return;
    try {
      const r: any = await firstValueFrom(this.http.get(`/api/kreta-atvitel/fiokok/${this.intezmeny}`));
      this.fiokok = Array.isArray(r) ? r : [];
    } catch { this.fiokok = []; }
  }

  /** Egy korábban indított (még futó vagy kész) önteszt állapota – oldalváltás után is. */
  async allapotBetolt() {
    if (!this.intezmeny) return;
    try {
      this.onteszt = await firstValueFrom(this.http.get(`/api/kreta-atvitel/onteszt/${this.intezmeny}`));
      if (this.onteszt?.fut) this.kovet();
    } catch { /* nincs korábbi */ }
  }

  /** `kreta: false` – csak az adatbázis-ellenőrzések, KRÉTA-belépés nélkül. */
  async ontesztIndit(kreta = true) {
    if (this.inditas || this.onteszt?.fut) return;
    this.inditas = true;
    try {
      const r: any = await firstValueFrom(this.http.post(`/api/kreta-atvitel/onteszt/${this.intezmeny}`,
        { allomany: this.tanarFiok || '', admin_allomany: this.adminFiok || '', kreta }));
      this.onteszt = r?.onteszt || { fut: true };
      this.kovet();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az önteszt nem indítható.', 'error');
    } finally {
      this.inditas = false;
    }
  }

  private kovet() {
    this.leallit();
    this.idozito = setInterval(async () => {
      try {
        this.onteszt = await firstValueFrom(this.http.get(`/api/kreta-atvitel/onteszt/${this.intezmeny}`));
        if (!this.onteszt?.fut) { this.leallit(); this.feladatBetolt(); }
      } catch { this.leallit(); }
    }, 2500);
  }

  private leallit() { if (this.idozito) { clearInterval(this.idozito); this.idozito = null; } }

  /** Az eredmény funkciónként; a nem ismert adatkör a végére kerül. */
  csoportok(e: any): { kulcs: string; cim: string; sorok: any[]; allapot: string }[] {
    const lista: any[] = e?.ellenorzesek || [];
    const rang: Record<string, number> = { hiba: 3, figyelmeztetes: 2, nincs_adat: 1, rendben: 0 };
    const ismert = new Set(this.FUNKCIOK.map((f) => f.kulcs));
    const funkciok = [...this.FUNKCIOK, ...[...new Set(lista.map((c) => c.adatkor).filter((k) => !ismert.has(k)))].map((k) => ({ kulcs: k, cim: k }))];
    return funkciok
      .map((f) => {
        const sorok = lista.filter((c) => c.adatkor === f.kulcs);
        const allapot = sorok.reduce((a, c) => (rang[c.allapot] > rang[a] ? c.allapot : a), 'rendben');
        return { ...f, sorok, allapot };
      })
      .filter((f) => f.sorok.length);
  }

  /** A le nem futott funkciók – ezekről az önteszt nem tudott mondani semmit (pl. elbukott a belépés). */
  hianyzoFunkciok(e: any): string[] {
    const van = new Set((e?.ellenorzesek || []).map((c: any) => c.adatkor));
    return this.FUNKCIOK.filter((f) => !van.has(f.kulcs)).map((f) => f.cim);
  }

  szin(o: string): string {
    return o === 'hiba' ? 'danger' : o === 'figyelmeztetes' ? 'warning' : o === 'rendben' ? 'success' : 'medium';
  }

  ido(v: any): string {
    if (!v) return '–';
    const d = new Date(v);
    return isNaN(d.getTime()) ? String(v) : d.toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'short' });
  }
}
